import React from 'react';
import { CreditCard, Loader2, XCircle, CheckCircle } from 'lucide-react';
import { toast } from 'react-toastify';
import { disconnectStripe, getStripeConnectUrl, StripeConnection } from '../../../api/stripeApi';

interface PaymentSettingsTabProps {
  connection: StripeConnection | null;
  loading: boolean;
  onConnectionChange: (connection: StripeConnection | null) => void;
}

export const PaymentSettingsTab: React.FC<PaymentSettingsTabProps> = ({ connection, loading, onConnectionChange }) => {
  const [connecting, setConnecting] = React.useState(false);
  const [disconnecting, setDisconnecting] = React.useState(false);

  const handleConnect = async () => {
    setConnecting(true);
    try {
      const url = await getStripeConnectUrl();
      if (url) window.location.href = url;
    } catch {
      toast.error('Failed to connect Stripe');
      setConnecting(false);
    }
  };

  const handleDisconnect = async () => {
    if (!window.confirm('Are you sure you want to disconnect Stripe?')) return;
    setDisconnecting(true);
    try {
      await disconnectStripe();
      onConnectionChange(null);
      toast.success('Stripe disconnected successfully');
    } catch {
      toast.error('Failed to disconnect Stripe');
    } finally {
      setDisconnecting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center rounded-2xl border border-strivre-lightnavy bg-slate-50/40 p-8 text-slate-500">
        <Loader2 className="mr-2 h-5 w-5 animate-spin" />
        Loading Stripe settings...
      </div>
    );
  }

  return (
    <div className="space-y-5 text-left">
      <div className="rounded-2xl border border-strivre-lightnavy bg-white p-6 shadow-sm">
        <div className="flex items-center gap-3 mb-4">
          <div className="rounded-xl bg-strivre-lightburgundy p-2 text-strivre-burgundy">
            <CreditCard size={18} />
          </div>
          <div>
            <h4 className="font-semibold text-slate-900">Stripe Payments</h4>
            <p className="text-sm text-slate-500">Connect your business Stripe account to receive invoice payments.</p>
          </div>
        </div>

        {connection?.isConnected ? (
          <div className="rounded-2xl border border-green-200 bg-green-50 p-5">
            <div className="flex items-center gap-2 text-green-700">
              <CheckCircle size={18} />
              <span className="font-semibold">Stripe Connected</span>
            </div>
            <div className="mt-4 space-y-2 text-sm text-green-800">
              <p><span className="font-semibold">Stripe Account ID:</span> {connection.stripeAccountId || 'acct_xxxxxxxxx'}</p>
              <p><span className="font-semibold">Connected on:</span> {connection.connectedAt ? new Date(connection.connectedAt).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' }) : 'N/A'}</p>
            </div>
            <button
              onClick={handleDisconnect}
              disabled={disconnecting}
              className="mt-4 flex items-center gap-2 rounded-xl border border-red-200 bg-red-50 px-4 py-2 text-sm font-semibold text-red-700"
            >
              {disconnecting ? <Loader2 className="h-4 w-4 animate-spin" /> : <XCircle size={16} />}
              {disconnecting ? 'Disconnecting...' : 'Disconnect Stripe'}
            </button>
          </div>
        ) : (
          <div className="rounded-2xl border border-red-200 bg-red-50 p-5">
            <div className="flex items-center gap-2 text-red-700">
              <XCircle size={18} />
              <span className="font-semibold">Stripe Not Connected</span>
            </div>
            <button
              onClick={handleConnect}
              disabled={connecting}
                className="mt-4 flex items-center gap-2 rounded-xl bg-strivre-burgundy px-4 py-2 text-sm font-semibold text-white"
            >
              {connecting ? <Loader2 className="h-4 w-4 animate-spin" /> : <CreditCard size={16} />}
              {connecting ? 'Connecting...' : 'Connect Stripe'}
            </button>
          </div>
        )}
      </div>

      <div className="rounded-2xl border border-strivre-lightburgundy bg-strivre-lightburgundy p-5">
        <h4 className="font-semibold text-strivre-burgundy">Why connect Stripe?</h4>
        <ul className="mt-3 space-y-2 text-sm text-strivre-burgundy">
          <li className="flex gap-2"><span>✓</span><span>Receive invoice payments online</span></li>
          <li className="flex gap-2"><span>✓</span><span>Funds go directly to your Stripe account</span></li>
          <li className="flex gap-2"><span>✓</span><span>Automatic invoice payment tracking</span></li>
          <li className="flex gap-2"><span>✓</span><span>Secure Stripe-hosted checkout</span></li>
        </ul>
      </div>
    </div>
  );
};
